import { Animated } from 'react-native';

import theme from '@themes/theme';

/**
 * TA-10632: presentation logic for the web project-onboarding card stack.
 *
 * The stack floats over the app in the bottom-right corner, so every piece of
 * it either has to be visible or has to be unmounted — an invisible container
 * still swallows clicks on RN-Web. Kept out of MainPanel.web.tsx so the fade
 * behaviour is testable without mounting the whole web app shell.
 */

// Geometry of the stack: every queued card behind the top one is drawn as a
// sliver peeking out from under it, each a little narrower and a little lower
// than the one in front.
export const ONBOARDING_STACK_INSET = theme.spacing.s;
export const ONBOARDING_STACK_PEEK = theme.spacing.xs;
export const ONBOARDING_STACK_LAYER_HEIGHT = theme.spacing.xl;

export const ONBOARDING_FADE_IN_MS = 250;
export const ONBOARDING_FADE_OUT_MS = 200;

export const onboardingLayerStyle = (index: number) => ({
  position: 'absolute' as const,
  left: (index + 1) * ONBOARDING_STACK_INSET,
  right: (index + 1) * ONBOARDING_STACK_INSET,
  bottom: -(index + 1) * ONBOARDING_STACK_PEEK,
  height: ONBOARDING_STACK_LAYER_HEIGHT,
  // Under the top card (zIndex 3 in MainPanel), and under the layer in front
  // of it.
  zIndex: 2 - index,
  // Opaque, not faded: a translucent sliver just tints whatever list row sits
  // behind it grey, which reads as a smudge rather than as another card. Depth
  // comes from the border plus a shadow far lighter than the top card's —
  // these are only edges peeking out, so a deep one under each reads as
  // clutter.
  shadowColor: theme.colors.black,
  shadowOffset: { width: 0, height: 3 },
  shadowOpacity: 0.08,
  shadowRadius: 8,
  elevation: 2,
});

/**
 * Shared by both chips flanking the stack ("Dismiss All" above it, "+N more"
 * below it). They are white on white app content, so each needs a shadow to
 * read as a separate surface — lighter than the top card's, which is the only
 * part of the stack meant to sit forward.
 */
export const onboardingChipShadow = {
  shadowColor: theme.colors.black,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.12,
  shadowRadius: 6,
  elevation: 2,
};

/**
 * Fades the stack out, runs `action`, then puts the stack's opacity straight
 * back to 1.
 *
 * TA-10632: the container is only unmounted once the queue empties, so an
 * action that *replaces* the top card instead of removing the last one — "Go
 * to Project" on a multi-card stack, or a dismiss that races a subscription
 * push — would otherwise leave it mounted at `opacity: 0`. The cards left
 * behind would be invisible (so never seen, never dismissed, and resurfaced on
 * every load) and the container would go on swallowing clicks over the
 * bottom-right of the page, because `opacity: 0` does not imply
 * `pointer-events: none` on RN-Web.
 *
 * Restored without a fade on purpose: the cards left behind were already on
 * screen, so fading them back in reads as a blink.
 */
export const fadeOutThenAction = (
  fadeAnim: Animated.Value,
  action: () => void
): void => {
  Animated.timing(fadeAnim, {
    toValue: 0,
    duration: ONBOARDING_FADE_OUT_MS,
    useNativeDriver: true,
  }).start(() => {
    action();
    fadeAnim.setValue(1);
  });
};
